import { createGlobalStyle } from 'styled-components'
import * as sc from 'constants/style'
import { breakpoint } from 'utils/breakpoint'
import Breakdown from 'components/ui/breakdown'

export const TypographyCss = createGlobalStyle`
 /*
  * !important on font-family/color: a federated remote's own bare CSS import
  * (e.g. semantic-ui-css, whose default theme is Lato + blue links) can set
  * this same \`body {}\` selector once its chunk loads, and it never unloads
  * on unmount/navigation — so without this, the last remote visited wins
  * for the rest of the session, on every host page.
  */
 body {
    font-family: 'Open Sans', 'Helvetica', 'Helvetica Neue', sans-serif !important;
    line-height: 1.6em;
    font-size: ${sc.BASE_FONT_SIZE}px;
    font-weight: 300;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.5;
    letter-spacing: 0.6px;
    color: ${sc.BASE_COLOR_HEX} !important;
  }

  /*
   * Buttons and fields take the page font: browsers give them their own (Arial) otherwise.
   * :where keeps it at zero specificity, so any component that sets a font still wins.
   */
  :where(button, input, select, textarea) {
    font-family: inherit;
  }

  strong {
    font-weight: 600;
  }

  /* Library Buttons rendered as links carry type="button" and have their own hover. */
  a:not(:where(.mf-remote-route a, [type='button'])) {
    &:hover {
      opacity: .6;
      ${Breakdown} {
        opacity: .6;
      }
    }
    &:active, &.active {
      opacity: 1;
      ${Breakdown} {
        opacity: 1;
      }
    }
  }

  /*
   * Headings are excluded on remote routes (<body class="mf-remote-route">, set by
   * HostGlobalStyles). These rules are global and h3 carries !important, so without
   * the exclusion they reach a remote's headings and flatten the remote's own scale.
   *
   * The marker is on <body>, not a wrapper around the remote subtree: the library's
   * modals createPortal into document.body, so remote content rendered in a modal
   * sits outside any wrapper we could put in the route tree.
   *
   * Mounting cannot be used to gate this: under SSR the server-rendered copy of
   * this sheet is rehydrated into a group the client's createGlobalStyle never
   * claims, so unmounting on a remote route removes only the client's copy and
   * orphans the server's permanently. The exclusion has to live in the selector.
   *
   * :where() is required, not cosmetic. A bare :not(.mf-remote-route h2) inherits its
   * argument's specificity — (0,1,2) instead of (0,0,1) — which makes these rules
   * outrank the component styles meant to override them (h2/h4/h5 carry no
   * !important). :where() contributes zero, so specificity is byte-for-byte unchanged.
   */
  h2:not(:where(.mf-remote-route h2)) {
    font-size: 34px;
    font-weight: 800;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.18;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    margin-bottom: ${sc.BASE_MARGIN * 2}px;
    ${breakpoint.below('mobile')} {
      font-size: 22px;
    }
  }

  h3:not(:where(.mf-remote-route h3)) {
    font-size: 22px !important;
    font-weight: 800;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.27;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    margin-bottom: ${sc.BASE_MARGIN * 2}px;
  }

  h4:not(:where(.mf-remote-route h4)) {
    font-size: 20px;
    font-weight: 800;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.27;
    letter-spacing: 0.8px;
    margin-bottom: ${sc.BASE_MARGIN * 2}px;
  }

  h5:not(:where(.mf-remote-route h5)) {
    font-size: 18px;
    font-weight: 700;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.27;
    letter-spacing: 0.8px;
    margin-bottom: ${sc.BASE_MARGIN * 2}px;
  }
`

export default TypographyCss
